import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo, useEffect } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { BaseRow } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { IProfile } from '~/utils/interface/auth';
import { useAppDispatch } from '~/redux/hooks';
import { handleEditSetting, handleGetProfile } from '~/thunks/auth/authThunk';
import { accountActions } from '~/thunks/account/accountSlice';

type EditAccountSettingDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  mode?: string;
  title?: string;
  data?: IProfile;
  children?: React.ReactNode;
};

const initialValues: IProfile = {
  email: '',
};

const AccountSettingEmailDialog = (props: EditAccountSettingDialogProps) => {
  //#region Destructuring Props
  const { mode, data } = props;
  //#endregion Destructuring Props
  const dispatch = useAppDispatch();
  //#region Declare Hook
  const { t } = useTranslation(['admin/account', 'admin/common']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State
  //#region Implement Hook
  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('email', cast(data?.email));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);
  //#endregion Implement Hook
  //#region Handle Function
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    form.setFieldValue('email', cast(data?.email));
  };

  const handleCancel = () => {
    handleSetOpen(false);
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleEditSetting({ params: values })).unwrap();
          dispatch(accountActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
          await dispatch(handleGetProfile()).unwrap();
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  //#endregion Handle Function

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {t('settingAccount.changeEmail')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px]'>
            <Box className='w-full'>
              <InputLabel> {t('settingAccount.email')}</InputLabel>
              <TextField
                name='email'
                placeholder={t('settingAccount.email')}
                value={form.values?.email}
                onChange={form.handleChange}
                error={Boolean(form.errors.email)}
                helperText={form.errors.email}
              />
            </Box>
          </BaseRow>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AccountSettingEmailDialog);
